import { useCallback, useEffect, useState } from 'react';

// antd 图片预览的左右翻页和关闭按钮默认固定在整个屏幕的左右两侧/右上角，大屏上离照片很远。
// 这里按照片实际显示的位置，把它们挪到照片两侧和右上角（照片缩放/旋转/切换后会跟着调整）。
// 依赖 antd 预览的类名（ant-image-preview-*）；找不到这些元素时什么都不做，保持 antd 默认位置。

const PREVIEW_IMG = '.ant-image-preview-wrap:not([style*="display: none"]) .ant-image-preview-img';
const SWITCH_LEFT = '.ant-image-preview-switch-left';
const SWITCH_RIGHT = '.ant-image-preview-switch-right';
const CLOSE = '.ant-image-preview-close';

const SWITCH_SIZE = 44; // antd 翻页按钮直径
const CLOSE_SIZE = 44;
const GAP = 12;
const EDGE = 8;

const setStyles = (el: Element | null, styles: Record<string, string>) => {
  if (!el) return;
  const style = (el as HTMLElement).style;
  Object.keys(styles).forEach(key => { (style as any)[key] = styles[key]; });
};

const clamp = (value: number, low: number, high: number) => Math.max(low, Math.min(high, value));

/** 把翻页/关闭按钮贴近当前显示的照片。返回是否找到了可见的照片。 */
export function alignImagePreviewControls(): boolean {
  const img = document.querySelector(PREVIEW_IMG);
  if (!img) return false;
  const rect = img.getBoundingClientRect();
  if (!rect.width || !rect.height) return false;
  const vw = window.innerWidth || document.documentElement.clientWidth;
  const vh = window.innerHeight || document.documentElement.clientHeight;

  // 左右翻页：在照片左右两侧，竖向仍居中（antd 默认就是垂直居中）
  const left = clamp(rect.left - SWITCH_SIZE - GAP, EDGE, vw - SWITCH_SIZE - EDGE);
  const right = clamp(vw - rect.right - SWITCH_SIZE - GAP, EDGE, vw - SWITCH_SIZE - EDGE);
  document.querySelectorAll(SWITCH_LEFT).forEach(el => setStyles(el, { left: `${left}px`, right: 'auto' }));
  document.querySelectorAll(SWITCH_RIGHT).forEach(el => setStyles(el, { right: `${right}px`, left: 'auto' }));

  // 关闭：照片右上角外侧；照片顶到屏幕上沿时贴着屏幕上沿
  const closeRight = clamp(vw - rect.right, EDGE, vw - CLOSE_SIZE - EDGE);
  const closeTop = clamp(rect.top - CLOSE_SIZE - GAP, EDGE, vh - CLOSE_SIZE - EDGE);
  document.querySelectorAll(CLOSE).forEach(el => setStyles(el, { right: `${closeRight}px`, top: `${closeTop}px` }));
  return true;
}

/** 预览关闭后清掉我们写的位置，下次打开时重新计算 */
export function resetImagePreviewControls() {
  [SWITCH_LEFT, SWITCH_RIGHT, CLOSE].forEach(selector => {
    document.querySelectorAll(selector).forEach(el => setStyles(el, { left: '', right: '', top: '' }));
  });
}

/**
 * 给 antd 的 Image.PreviewGroup 用：把返回的函数传给 preview.onVisibleChange。
 * 预览打开期间每隔一小会儿（和窗口大小变化时）重新对齐，覆盖加载、切换、缩放、旋转。
 */
export function useAlignedImagePreview(): (visible: boolean) => void {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) {
      resetImagePreviewControls();
      return undefined;
    }
    alignImagePreviewControls();
    const timer = window.setInterval(alignImagePreviewControls, 80);
    window.addEventListener('resize', alignImagePreviewControls);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('resize', alignImagePreviewControls);
    };
  }, [open]);
  return useCallback((visible: boolean) => setOpen(visible), []);
}
